import { computed } from 'vue'
import type { QuotationsState } from './state'
import type { QuotationStatus } from '@/types'

export const createGetters = (state: QuotationsState) => {
  const filteredQuotations = computed(() => {
    let result = [...state.quotations]

    // Filter by search term
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (q) =>
          q.projectName.toLowerCase().includes(search) ||
          q.description.toLowerCase().includes(search)
      )
    }

    // Filter by status
    if (state.filters.status) {
      result = result.filter((q) => q.status === state.filters.status)
    }

    // Filter by organization
    if (state.filters.organizationId) {
      result = result.filter((q) => q.organizationId === state.filters.organizationId)
    }

    return result
  })

  const quotationsByStatus = computed(() => {
    const grouped: Record<QuotationStatus, typeof state.quotations> = {
      requested: [],
      analyzing: [],
      sent: [],
      accepted: [],
      rejected: [],
      expired: [],
    }

    state.quotations.forEach((q) => {
      if (grouped[q.status]) {
        grouped[q.status].push(q)
      }
    })

    return grouped
  })

  const requestedQuotations = computed(() => {
    return state.quotations.filter((q) => q.status === 'requested')
  })

  const sentQuotations = computed(() => {
    return state.quotations.filter((q) => q.status === 'sent')
  })

  const acceptedQuotations = computed(() => {
    return state.quotations.filter((q) => q.status === 'accepted')
  })

  const totalQuotedAmount = computed(() => {
    return state.quotations
      .filter((q) => q.status === 'accepted' && q.quotedAmount)
      .reduce((sum, q) => sum + (q.quotedAmount || 0), 0)
  })

  const averageQuotedAmount = computed(() => {
    const quotedOnes = state.quotations.filter((q) => q.quotedAmount)
    if (quotedOnes.length === 0) return 0
    return quotedOnes.reduce((sum, q) => sum + (q.quotedAmount || 0), 0) / quotedOnes.length
  })

  const conversionRate = computed(() => {
    const total = state.quotations.length
    if (total === 0) return 0
    const accepted = state.quotations.filter((q) => q.status === 'accepted').length
    return (accepted / total) * 100
  })

  return {
    filteredQuotations,
    quotationsByStatus,
    requestedQuotations,
    sentQuotations,
    acceptedQuotations,
    totalQuotedAmount,
    averageQuotedAmount,
    conversionRate,
  }
}
